import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
  Alert,
  Modal,
} from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { JobCardsTable } from '../../components/maintenance/JobCardsTable';
import theme from '../../theme';
import { JobCard } from '../../types';
import { Ionicons } from '@expo/vector-icons';
import { useNavigationContext } from '../../navigation/NavigationContext';
import { useJobCardContext } from '../../contexts/JobCardContext';
import { supabase } from '../../api/supabase';
import { useAuthStore } from '../../stores/authStore';
import { generateAndDownloadPDF } from '../../services/pdfGenerator';

export const JobCardsScreen = () => {
  const navigation = useNavigation<any>();
  const { setCurrentRoute } = useNavigationContext();
  const { setSelectedJobCardId, setEditingJobCard } = useJobCardContext();
  const isWeb = Platform.OS === 'web';
  const { user } = useAuthStore();
  // For web platform, check if navigate function exists instead of relying on state
  const navigationState = useNavigationState(state => state);
  const isNavigationReady = navigation && typeof navigation.navigate === 'function';
  const { jobCards, loading, error, fetchJobCards, deleteJobCard, updateJobCard } = useMaintenanceStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCard | null>(null);
  const [returnDate, setReturnDate] = useState('');

  useEffect(() => {
    fetchJobCards();
  }, []);

  const handleRefresh = () => {
    fetchJobCards();
  };

  const handleViewJobCard = (jobCard: JobCard) => {
    if (isWeb) {
      setSelectedJobCardId(String(jobCard.id));
      setCurrentRoute('MaintenanceDetails');
    } else {
      if (!isNavigationReady) return;
      navigation.navigate('MaintenanceDetails', { jobCardId: jobCard.id });
    }
  };

  const handleEditJobCard = (jobCard: JobCard) => {
    if (isWeb) {
      setEditingJobCard(jobCard);
      setCurrentRoute('AddJobCard');
    } else {
      if (!isNavigationReady) return;
      navigation.navigate('AddJobCard', { editingCard: jobCard });
    }
  };

  const handleAddJobCard = () => {
    if (isWeb) {
      console.log('Web: Navigating to AddJobCard using setCurrentRoute');
      setEditingJobCard(null); // Clear editing card when adding new
      setCurrentRoute('AddJobCard');
    } else {
      console.log('Mobile: Navigating to AddJobCard using navigation.navigate');
      if (!isNavigationReady) {
        console.warn('Navigation not ready');
        return;
      }
      navigation.navigate('AddJobCard');
    }
  };

  const handleDeleteJobCard = async (jobCard: JobCard) => {
    console.log('🗑️ Delete button clicked for job card:', jobCard.jobNumber, 'ID:', jobCard.id);
    
    // Check user permission for DELETE_JOB_CARDS
    const { useAuthStore } = require('../../stores/authStore');
    const { PERMISSIONS } = require('../../constants/permissions');
    const hasDeletePermission = useAuthStore.getState().hasPermission(PERMISSIONS.DELETE_JOB_CARDS);
    
    if (!hasDeletePermission) {
      window.alert('ليس لديك صلاحية لحذف بطاقات العمل\nYou do not have permission to delete job cards');
      return;
    }
    
    // Web-compatible confirmation
    const confirmed = window.confirm(
      `Are you sure you want to delete job card ${jobCard.jobNumber}?\n\nThis will set the vehicle back to available status.`
    );
    
    if (!confirmed) {
      console.log('❌ Deletion cancelled by user');
      return;
    }
    
    try {
      console.log('🗑️ Starting deletion process for job card:', jobCard.id);
      
      // Delete job card
      console.log('🗑️ Calling deleteJobCard API...');
      await deleteJobCard(String(jobCard.id));
      console.log('✅ Job card deleted from database');
      
      // Find vehicle and set back to available
      // Remove any vehicle code prefix (e.g., "4-22809" -> "22809")
      const plateWithoutCode = jobCard.licensePlate.includes('-') 
        ? jobCard.licensePlate.split('-').slice(1).join('-') 
        : jobCard.licensePlate;
      
      console.log('🔍 Looking for vehicle with plate:', plateWithoutCode);
      const { data: vehicles, error: vehicleError } = await supabase
        .from('vehicles')
        .select('id')
        .eq('license_plate', plateWithoutCode)
        .limit(1);
      
      if (vehicleError) {
        console.error('❌ Error finding vehicle:', vehicleError);
      } else if (vehicles && vehicles.length > 0) {
        console.log('✅ Found vehicle:', vehicles[0].id);
        const { error: updateError } = await supabase
          .from('vehicles')
          .update({ car_status: 'available' })
          .eq('id', vehicles[0].id);
        
        if (updateError) {
          console.error('❌ Error updating vehicle status:', updateError);
        } else {
          console.log('✅ Vehicle set to available');
        }
      } else {
        console.warn('⚠️ No vehicle found with plate:', jobCard.licensePlate);
      }
      
      // Log activity
      console.log('📝 Logging activity...');
      const { error: logError } = await supabase.from('activity_log').insert({
        entity_type: 'vehicle',
        entity_id: String(jobCard.id),
        action_type: 'delete',
        description: `Job card ${jobCard.jobNumber} deleted`,
        changes: {
          jobNumber: jobCard.jobNumber,
          licensePlate: jobCard.licensePlate,
          makeModel: jobCard.makeModel,
          jobStatus: jobCard.jobStatus,
          deletedBy: user?.name || user?.email || 'System'
        },
        user_id: user?.id || null,
        user_name: user?.name || user?.email || 'System',
        user_email: user?.email || null,
        user_role: user?.role || null
      });
      
      if (logError) {
        console.error('❌ Error logging activity:', logError);
      } else {
        console.log('✅ Activity logged');
      }
      
      alert('Job card deleted successfully');
      fetchJobCards();
    } catch (err) {
      console.error('❌ Error deleting job card:', err);
      alert('Failed to delete job card');
    }
  };

  const handleMarkComplete = (jobCard: JobCard) => {
    setSelectedJobCard(jobCard);
    setReturnDate(new Date().toISOString().split('T')[0]);
    setShowMarkCompleteModal(true);
  };

  const handleConfirmMarkComplete = async () => {
    if (!selectedJobCard || !returnDate) {
      Alert.alert(
        'خطأ / Error',
        'الرجاء إدخال تاريخ الإرجاع\nPlease enter a return date'
      );
      return;
    }

    try {
      // Update job card - use snake_case for database fields
      await updateJobCard(String(selectedJobCard.id), {
        job_status: 'completed',
        return_date: returnDate,
        received_by: 'Nazir Ahmed'
      });
      
      // Find vehicle and set to available
      const { data: vehicles, error: vehicleSelectError } = await supabase
        .from('vehicles')
        .select('id')
        .eq('license_plate', selectedJobCard.licensePlate)
        .limit(1);
      
      if (vehicleSelectError) {
        throw new Error(`Failed to find vehicle: ${vehicleSelectError.message}`);
      }
      
      if (vehicles && vehicles.length > 0) {
        const { error: vehicleUpdateError } = await supabase
          .from('vehicles')
          .update({ car_status: 'available' })
          .eq('id', vehicles[0].id);
        
        if (vehicleUpdateError) {
          throw new Error(`Failed to update vehicle status: ${vehicleUpdateError.message}`);
        }
      }
      
      // Log activity
      const { error: logError } = await supabase.from('activity_log').insert({
        entity_type: 'vehicle',
        entity_id: String(selectedJobCard.id),
        action_type: 'status_change',
        description: `Job card ${selectedJobCard.jobNumber} marked as complete`,
        changes: {
          jobNumber: selectedJobCard.jobNumber,
          licensePlate: selectedJobCard.licensePlate,
          makeModel: selectedJobCard.makeModel,
          returnDate: returnDate,
          receivedBy: 'Nazir Ahmed',
          completedBy: user?.name || user?.email || 'System'
        },
        user_id: user?.id || null,
        user_name: user?.name || user?.email || 'System',
        user_email: user?.email || null,
        user_role: user?.role || null
      });
      
      if (logError) {
        // Log error but don't fail the operation
        console.error('⚠️ Failed to log activity:', logError.message);
      }
      
      setShowMarkCompleteModal(false);
      setSelectedJobCard(null);
      
      if (Platform.OS === 'web') {
        alert('نجح / Success\n\nتم إكمال بطاقة العمل بنجاح\nJob card marked as complete successfully');
      } else {
        Alert.alert(
          'نجح / Success',
          'تم إكمال بطاقة العمل بنجاح\nJob card marked as complete successfully'
        );
      }
      
      fetchJobCards();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      
      if (Platform.OS === 'web') {
        alert(`خطأ / Error\n\nفشل في إكمال بطاقة العمل\nFailed to mark job card as complete\n\nDetails: ${errorMessage}`);
      } else {
        Alert.alert(
          'خطأ / Error',
          `فشل في إكمال بطاقة العمل\nFailed to mark job card as complete\n\nDetails: ${errorMessage}`
        );
      }
    }
  };

  const handlePrintJobCard = async (jobCard: JobCard) => {
    if (!isWeb) {
      Alert.alert('PDF Generation', 'PDF generation is only available on the web version of the app.');
      return;
    }

    try {
      console.log('� Generating PDF for job card:', jobCard.jobNumber);
      
      const printHtml = buildJobCardPrintHtml(jobCard);
      const fileName = `JobCard-${jobCard.jobNumber}-${Date.now()}`;
      
      await generateAndDownloadPDF(printHtml, fileName);
      
      if (Platform.OS === 'web') {
        alert('PDF Downloaded\n\nThe job card PDF has been downloaded to your device.');
      }
    } catch (err) {
      console.error('❌ PDF generation failed:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      // Check if it's a Gotenberg connectivity issue
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        Alert.alert(
          'PDF Service Unavailable',
          'The PDF generation service is not running.\n\nTo fix this:\n\n1. Make sure Docker Desktop is running\n2. Open terminal and run: docker-compose up -d\n3. Wait a moment and try again',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'PDF Generation Failed',
          `Could not generate PDF: ${errorMessage}\n\nPlease try again or contact support.`,
          [{ text: 'OK' }]
        );
      }
    }
  };

  const buildJobCardPrintHtml = (jobCard: JobCard): string => {
    const formatDate = (dateStr: string | null) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };

    // Get work type checkmarks
    const getCheckmark = (value: boolean) => value ? '✓' : '';

    // Get condition checkmarks  
    const getLightsOk = getCheckmark(jobCard.lightsOk !== false);
    const getWipersOk = getCheckmark(jobCard.wipersOk !== false);
    const getHornOk = getCheckmark(jobCard.hornOk !== false);
    const getAcOk = getCheckmark(jobCard.acOk !== false);
    const getTiresOk = getCheckmark(jobCard.tiresOk !== false);
    const getMirrorsOk = getCheckmark(jobCard.mirrorsOk !== false);

    // Logo URL
    const logoUrl = 'https://ik.imagekit.io/to2pwwjig/Logo.jpg?updatedAt=1750740262665';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Job Card - ${jobCard.jobNumber}</title>
        <style>
          @page {
            size: A4;
            margin: 10mm;
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 15px;
            font-size: 12px;
            max-width: 900px;
            margin: 0 auto;
          }
          /* Header matching JobCardFormA4 */
          .header {
            background-color: #455A64;
            color: white;
            padding: 15px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            border-radius: 4px;
          }
          .logo-area {
            display: flex;
            align-items: center;
            flex: 1;
          }
          .logo {
            height: 40px;
            width: 40px;
            border-radius: 3px;
            margin-right: 10px;
          }
          .card-title {
            font-size: 18px;
            font-weight: bold;
            color: white;
          }
          .job-number {
            background-color: rgba(255, 255, 255, 0.1);
            border-bottom: 2px solid rgba(255, 255, 255, 0.5);
            color: white;
            font-size: 16px;
            font-weight: bold;
            padding: 5px 10px;
            min-width: 220px;
            text-align: left;
          }
          /* Top sections container - side by side */
          .top-sections-container {
            display: flex;
            gap: 10px;
            margin-bottom: 6px;
          }
          .top-sections-container > .section {
            flex: 1;
          }
          /* Section styling */
          .section {
            border: 1px solid #ddd;
            padding: 8px;
            border-radius: 4px;
            margin-bottom: 10px;
          }
          .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #455A64;
            margin-bottom: 5px;
            border-bottom: 1px solid #eee;
            padding-bottom: 3px;
          }
          .form-row {
            display: flex;
            gap: 8px;
            margin-bottom: 4px;
          }
          .form-group {
            flex: 1;
            margin-bottom: 4px;
          }
          .label {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            margin-bottom: 2px;
          }
          .value {
            border: 1px solid #ddd;
            min-height: 22px;
            padding: 2px 5px;
            font-size: 12px;
            border-radius: 2px;
            background: #fff;
          }
          .text-area {
            min-height: 30px;
            padding-top: 5px;
          }
          /* Vehicle Condition Layout */
          .vehicle-condition-layout {
            display: flex;
            gap: 10px;
            align-items: flex-start;
          }
          .vehicle-diagram-container {
            width: 436px;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 3px;
            background: #f9f9f9;
          }
          .vehicle-diagram {
            width: 100%;
            height: 170px;
            object-fit: contain;
          }
          .vehicle-condition-details {
            flex: 1;
          }
          /* Checkboxes grid */
          .work-types-grid, .condition-checks-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 2px;
            margin-bottom: 6px;
          }
          .checkbox-item {
            display: flex;
            align-items: center;
            margin-bottom: 5px;
            margin-right: 6px;
            min-width: 90px;
          }
          .checkbox {
            width: 16px;
            height: 16px;
            border: 1px solid #546E7A;
            border-radius: 2px;
            margin-right: 6px;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            background: #fff;
            flex-shrink: 0;
          }
          .checkbox-checked {
            background: #546E7A;
          }
          .checkmark {
            color: white;
            font-size: 12px;
            font-weight: bold;
          }
          .checkbox-label {
            font-size: 12px;
            color: #333;
          }
          /* Footer */
          .footer {
            margin-top: 15px;
            border-top: 1px solid #ddd;
            padding-top: 10px;
            display: flex;
            justify-content: space-between;
          }
          .footer-text {
            font-size: 11px;
            color: #555;
          }
          .footer-bold {
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <!-- Header matching JobCardFormA4 -->
        <div class="header">
          <div class="logo-area">
            <img src="${logoUrl}" alt="Logo" class="logo" />
            <div class="card-title">Internal STSC Management Job Card</div>
          </div>
          <div class="job-number">${jobCard.jobNumber || 'Auto-generated...'}</div>
        </div>

        <!-- Top sections container - Request Information & Vehicle Information side by side -->
        <div class="top-sections-container">
          <!-- Request Information Section -->
          <div class="section">
            <div class="section-title">Request Information</div>
            <div class="form-row">
              <div class="form-group">
                <div class="label">Request Date:</div>
                <div class="value">${formatDate(jobCard.requestDate)}</div>
              </div>
              <div class="form-group">
                <div class="label">Required By:</div>
                <div class="value">${jobCard.requiredBy || ''}</div>
              </div>
            </div>
            <div class="form-group">
              <div class="label">Requested By:</div>
              <div class="value">${jobCard.requestedBy || ''}</div>
            </div>
          </div>

          <!-- Vehicle Information Section -->
          <div class="section">
            <div class="section-title">Vehicle Information</div>
            <div class="form-row">
              <div class="form-group">
                <div class="label">License Plate:</div>
                <div class="value">${jobCard.licensePlate}</div>
              </div>
              <div class="form-group">
                <div class="label">Make & Model:</div>
                <div class="value">${jobCard.makeModel || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <div class="label">Year:</div>
                <div class="value">${jobCard.year || ''}</div>
              </div>
              <div class="form-group">
                <div class="label">Current Mileage:</div>
                <div class="value">${jobCard.mileage || ''}</div>
              </div>
              <div class="form-group">
                <div class="label">Assigned Driver:</div>
                <div class="value">${jobCard.driver || ''}</div>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <div class="label">Fuel Level:</div>
                <div class="value">${jobCard.fuelLevel || '50'}%</div>
              </div>
              <div class="form-group">
                <div class="label">Vehicle Keys:</div>
                <div class="value">${jobCard.keys || '-'}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Vehicle Condition Section -->
        <div class="section">
          <div class="section-title">Vehicle Condition</div>
          <div class="vehicle-condition-layout">
            <div class="vehicle-diagram-container">
              <img src="https://ik.imagekit.io/to2pwwjig/Tesla%20Model%20S%20-%20Free%20CAD%20Drawings.jpeg?updatedAt=1750739302834" alt="Vehicle Diagram" class="vehicle-diagram" />
            </div>
            <div class="vehicle-condition-details">
              <!-- Type of Work -->
              <div class="form-group">
                <div class="label">Type of Work:</div>
                <div class="work-types-grid">
                  <div class="checkbox-item">
                    <div class="checkbox ${jobCard.underMaintenance ? 'checkbox-checked' : ''}">
                      ${jobCard.underMaintenance ? '<span class="checkmark">✓</span>' : ''}
                    </div>
                    <span class="checkbox-label">Under Maintenance</span>
                  </div>
                  <div class="checkbox-item">
                    <div class="checkbox ${jobCard.accidentRepair ? 'checkbox-checked' : ''}">
                      ${jobCard.accidentRepair ? '<span class="checkmark">✓</span>' : ''}
                    </div>
                    <span class="checkbox-label">Accident Repair</span>
                  </div>
                  <div class="checkbox-item">
                    <div class="checkbox ${jobCard.underMaintenanceAlQouz ? 'checkbox-checked' : ''}">
                      ${jobCard.underMaintenanceAlQouz ? '<span class="checkmark">✓</span>' : ''}
                    </div>
                    <span class="checkbox-label">Under Maintenance - Al Qouz</span>
                  </div>
                  <div class="checkbox-item">
                    <div class="checkbox ${jobCard.underMaintenanceAgency ? 'checkbox-checked' : ''}">
                      ${jobCard.underMaintenanceAgency ? '<span class="checkmark">✓</span>' : ''}
                    </div>
                    <span class="checkbox-label">Under Maintenance - Agency</span>
                  </div>
                  <div class="checkbox-item">
                    <div class="checkbox ${jobCard.others ? 'checkbox-checked' : ''}">
                      ${jobCard.others ? '<span class="checkmark">✓</span>' : ''}
                    </div>
                    <span class="checkbox-label">Others</span>
                  </div>
                </div>
              </div>

              <!-- Condition Notes -->
              <div class="form-group">
                <div class="label">Condition Notes:</div>
                <div class="value text-area">${jobCard.conditionNotes || ''}</div>
              </div>

              <!-- Condition Checks Grid -->
              <div class="condition-checks-grid">
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.lightsOk !== false ? 'checkbox-checked' : ''}">
                    ${getLightsOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">Lights OK</span>
                </div>
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.wipersOk !== false ? 'checkbox-checked' : ''}">
                    ${getWipersOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">Wipers OK</span>
                </div>
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.hornOk !== false ? 'checkbox-checked' : ''}">
                    ${getHornOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">Horn OK</span>
                </div>
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.acOk !== false ? 'checkbox-checked' : ''}">
                    ${getAcOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">A/C OK</span>
                </div>
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.tiresOk !== false ? 'checkbox-checked' : ''}">
                    ${getTiresOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">Tires OK</span>
                </div>
                <div class="checkbox-item">
                  <div class="checkbox ${jobCard.mirrorsOk !== false ? 'checkbox-checked' : ''}">
                    ${getMirrorsOk ? '<span class="checkmark">✓</span>' : ''}
                  </div>
                  <span class="checkbox-label">Mirrors OK</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Work Required Section -->
        <div class="section">
          <div class="section-title">Work Required</div>
          <div class="form-group">
            <div class="label">Description of Work Required:</div>
            <div class="value text-area">${jobCard.workDescription || ''}</div>
          </div>
        </div>

        <!-- Handover Information Section -->
        <div class="section">
          <div class="section-title">Handover Information</div>
          <div class="form-row">
            <div class="form-group">
              <div class="label">Handover Date:</div>
              <div class="value">${formatDate(jobCard.handoverDate)}</div>
            </div>
            <div class="form-group">
              <div class="label">Expected Return Date:</div>
              <div class="value">${formatDate(jobCard.returnDate)}</div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <div class="label">Handed Over By:</div>
              <div class="value">${jobCard.handedBy || ''}</div>
            </div>
            <div class="form-group">
              <div class="label">Received By (Workshop):</div>
              <div class="value">${jobCard.receivedBy || ''}</div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <div class="label">Maintenance Dept. Signature:</div>
              <div class="value">
                ${jobCard.maintenanceSignatureSvg 
                  ? `<img src="${jobCard.maintenanceSignatureSvg}" alt="Maintenance Signature" style="max-width: 100%; height: 60px; object-fit: contain;" />`
                  : jobCard.maintenanceSignature || ''
                }
              </div>
            </div>
            <div class="form-group">
              <div class="label">Workshop Signature:</div>
              <div class="value">
                ${jobCard.workshopSignatureSvg 
                  ? `<img src="${jobCard.workshopSignatureSvg}" alt="Workshop Signature" style="max-width: 100%; height: 60px; object-fit: contain;" />`
                  : jobCard.workshopSignature || ''
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer">
          <div class="footer-text">
            <span class="footer-bold">Job Card #:</span> ${jobCard.jobNumber || '______'}
          </div>
          <div class="footer-text">
            <span class="footer-bold">Date Printed:</span> ${new Date().toLocaleDateString()}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Filter for current (active) job cards
  const currentJobCards = jobCards.filter(
    (jc) => jc.jobStatus === 'pending' || jc.jobStatus === 'in_progress'
  );

  // Filter for history (completed) job cards
  const historyJobCards = jobCards.filter(
    (jc) => jc.jobStatus === 'completed' || jc.jobStatus === 'handed_over'
  );

  // Apply search filter
  const filterJobCards = (cards: JobCard[]) => {
    return cards.filter((jobCard) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        (jobCard.jobNumber?.toString() || '').toLowerCase().includes(searchLower) ||
        (jobCard.licensePlate?.toLowerCase() || '').includes(searchLower) ||
        (jobCard.makeModel?.toLowerCase() || '').includes(searchLower)
      );
    });
  };

  const filteredCurrentJobs = filterJobCards(currentJobCards);
  const filteredHistoryJobs = filterJobCards(historyJobCards);

  const displayedJobs = activeTab === 'current' ? filteredCurrentJobs : filteredHistoryJobs;

  if (error) {
    return (
      <View style={styles.centered}>
        <Ionicons name="warning-outline" size={48} color={theme.colors.error} />
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={handleRefresh}>
          <Text style={styles.retryButtonText}>Try Again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View>
            <Text style={styles.title}>Job Cards</Text>
            <Text style={styles.subtitle}>
              {activeTab === 'current' 
                ? `${currentJobCards.length} active job cards`
                : `${historyJobCards.length} completed job cards`
              }
            </Text>
          </View>
          <TouchableOpacity
            style={styles.addButton}
            onPress={handleAddJobCard}
          >
            <Ionicons name="add" size={24} color={theme.colors.white} />
            <Text style={styles.addButtonText}>New Job Card</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search" size={20} color={theme.colors.textSecondary} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search by job #, plate, or vehicle..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor={theme.colors.textSecondary}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'current' && styles.tabActive]}
          onPress={() => setActiveTab('current')}
        >
          <Ionicons 
            name="time-outline" 
            size={20} 
            color={activeTab === 'current' ? theme.colors.primary : theme.colors.textSecondary} 
          />
          <Text style={[styles.tabText, activeTab === 'current' && styles.tabTextActive]}>
            Current
          </Text>
          <View style={[styles.badge, activeTab === 'current' && styles.badgeActive]}>
            <Text style={[styles.badgeText, activeTab === 'current' && styles.badgeTextActive]}>
              {currentJobCards.length}
            </Text>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'history' && styles.tabActive]}
          onPress={() => setActiveTab('history')}
        >
          <Ionicons 
            name="checkmark-done-outline" 
            size={20} 
            color={activeTab === 'history' ? theme.colors.primary : theme.colors.textSecondary} 
          />
          <Text style={[styles.tabText, activeTab === 'history' && styles.tabTextActive]}>
            History
          </Text>
          <View style={[styles.badge, activeTab === 'history' && styles.badgeActive]}>
            <Text style={[styles.badgeText, activeTab === 'history' && styles.badgeTextActive]}>
              {historyJobCards.length}
            </Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Content */}
      {loading && jobCards.length === 0 ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading job cards...</Text>
        </View>
      ) : displayedJobs.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons 
            name={activeTab === 'current' ? "construct-outline" : "checkmark-done-outline"} 
            size={64} 
            color={theme.colors.gray300} 
          />
          <Text style={styles.emptyText}>
            {searchQuery 
              ? 'No job cards match your search'
              : activeTab === 'current' 
                ? 'No active job cards'
                : 'No completed job cards'
            }
          </Text>
          <Text style={styles.emptySubtext}>
            {searchQuery 
              ? 'Try a different search term'
              : activeTab === 'current'
                ? 'Create a new job card to get started'
                : 'Completed job cards will appear here'
            }
          </Text>
          {!searchQuery && activeTab === 'current' && (
            <TouchableOpacity style={styles.emptyButton} onPress={handleAddJobCard}>
              <Text style={styles.emptyButtonText}>Create Job Card</Text>
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <JobCardsTable
          jobCards={displayedJobs}
          onRowPress={handleViewJobCard}
          onEdit={handleEditJobCard}
          onDelete={handleDeleteJobCard}
          onMarkComplete={handleMarkComplete}
          onPrint={handlePrintJobCard}
        />
      )}

      {/* Mark Complete Modal */}
      <Modal
        visible={showMarkCompleteModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowMarkCompleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Mark Job Card Complete</Text>
              <TouchableOpacity onPress={() => setShowMarkCompleteModal(false)}>
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                Job Card: <Text style={styles.modalTextBold}>{selectedJobCard?.jobNumber}</Text>
              </Text>
              <Text style={styles.modalText}>
                Vehicle: <Text style={styles.modalTextBold}>{selectedJobCard?.licensePlate} - {selectedJobCard?.makeModel}</Text>
              </Text>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Return Date *</Text>
                <TextInput
                  style={styles.dateInput}
                  value={returnDate}
                  onChangeText={setReturnDate}
                  placeholder="YYYY-MM-DD"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Received By</Text>
                <Text style={styles.inputValue}>Nazir Ahmed</Text>
              </View>
            </View>

            <View style={styles.modalFooter}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setShowMarkCompleteModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.confirmButton]}
                onPress={handleConfirmMarkComplete}
              >
                <Text style={styles.confirmButtonText}>Mark Complete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  header: {
    backgroundColor: theme.colors.white,
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    ...theme.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  subtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.sm,
  },
  addButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600',
  },
  searchContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    marginHorizontal: theme.spacing.md,
    marginVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.lg,
    padding: 4,
    ...theme.shadows.sm,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    gap: 8,
  },
  tabActive: {
    backgroundColor: theme.colors.primaryLight,
  },
  tabText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: '600',
  },
  tabTextActive: {
    color: theme.colors.primary,
  },
  badge: {
    backgroundColor: theme.colors.gray200,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 24,
    alignItems: 'center',
  },
  badgeActive: {
    backgroundColor: theme.colors.primary,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  badgeTextActive: {
    color: theme.colors.white,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    color: theme.colors.textSecondary,
    fontSize: theme.typography.sizes.md,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: theme.typography.sizes.md,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
  },
  retryButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.md,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xs,
    textAlign: 'center',
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.lg,
  },
  emptyButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.md,
  },
  emptyButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.md,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  modalBody: {
    padding: 16,
  },
  modalText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  modalTextBold: {
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  inputGroup: {
    marginTop: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  inputValue: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    padding: 12,
    backgroundColor: theme.colors.gray50,
    borderRadius: 6,
  },
  dateInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.gray100,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  confirmButton: {
    backgroundColor: theme.colors.success,
  },
  confirmButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.white,
  },
});
